import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  PagesByDomainQueryHookResult,
  useDeletePage2Mutation,
  usePagesByDomainQuery,
} from "../../../graphql/projects.hooks";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../components/heading";
import ImgFolder from "./img/folder.svg";
import ImgPage from "./img/test-page.png";
import FolderPageListGrid from "./FolderPageListGrid";
import FolderPageListTable from "./FolderPageListTable";
import Pagination from "../../../components/pagination";
import { useDebounceEffect } from "../../../hooks/useDebounceEffect";
import SearchBar from "../../../components/searchBar";
import NoPages from "./noPages";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import { Spinner } from "react-bootstrap";
import { useUserQuery } from "../../../graphql/menu.hooks";
import { IMoveItems } from "../../../types";
import {
  isHaveBeenSaved,
  isLoadingOfDeletion,
  moveItem,
} from "../../../graphql/state";
import { useReactiveVar } from "@apollo/client";
import DeletePageFolderModal from "../../../components/modals/deletePageFolderModal";
import { useMassDeleteFolderAndPageMutation } from "../../../graphql/page.hooks";
import { NotFoundPlug } from "../../../components/plugs/notFound/NotFoundPlug";
import { CustomToast } from "../../../components/inputs/toast";
import { ITEMS } from "../../../utils";

export interface PageProps {
  id?: number;
  title?: string;
  image?: string;
  type?: number;
  users?: PagesByDomainQueryHookResult["data"]["pagesByDomain"]["pagesWithoutFolder"][0]["userAccesses"];
  publishedAt?: string;
  publicUrl?: string;
}
export interface FolderProps {
  id?: number;
  title?: string;
  users?: PagesByDomainQueryHookResult["data"]["pagesByDomain"]["folders"][0]["userAccesses"];
  user?: NonNullable<ReturnType<typeof useUserQuery>["data"]>["user"];
}

export type DomainProps =
  PagesByDomainQueryHookResult["data"]["pagesByDomain"]["domain"]["userAccesses"];

const ProjectPage: React.FC = () => {
  const { t } = useTranslation();
  const { projectId, id } = useParams();
  const moveItemState = useReactiveVar(moveItem);
  const haveBeenSaved = useReactiveVar(isHaveBeenSaved);
  const isLoading = useReactiveVar(isLoadingOfDeletion);
  const projectSettings = JSON.parse(localStorage.getItem("mePageSettings"));
  const [currentPage, setCurrentPage] = useState(
    projectSettings?.projectPage?.currentPage || 1
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "table">(
    projectSettings?.projectPage?.viewMode || "grid"
  );
  const [limit, setLimit] = useState(projectSettings?.projectPage?.limit || 12);
  const [selectedItems, setSelectedItems] = useState<IMoveItems>({
    pageIds: [],
    folderIds: [],
  });
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  let deleteModalTitle = t("modal.deleteFiles");
  let deleteModalDescription = t("modal.deleteFilesText");
  let deleteModalAdditionText = t("projects.permanentlyFolder");

  if (selectedItems.folderIds.length === 1 && !selectedItems.pageIds.length) {
    deleteModalTitle = t("projects.deleteFolder");
    deleteModalDescription = t("projects.deleteFolderDescription");
  } else if (
    selectedItems.pageIds.length === 1 &&
    !selectedItems.folderIds.length
  ) {
    deleteModalTitle = t("profile.deleteAction");
    deleteModalDescription = t("projects.deletePageDescription");
    deleteModalAdditionText = t("projects.permanently");
  }

  useEffect(() => {
    if (
      !moveItemState.data.folderIds.length &&
      !moveItemState.data.pageIds.length
    ) {
      setSelectedItems({
        pageIds: [],
        folderIds: [],
      });
    }
  }, [moveItemState.data]);

  useEffect(() => {
    const settings = JSON.parse(localStorage.getItem("mePageSettings"));
    localStorage.setItem(
      "mePageSettings",
      JSON.stringify({
        ...settings,
        projectPage: { viewMode, limit, currentPage },
      })
    );
  }, [viewMode, limit, currentPage]);

  const {
    data: pagesByDomain,
    loading,
    refetch: refetchPagesByDomain,
  } = usePagesByDomainQuery({
    variables: {
      page: currentPage,
      domainId: Number(projectId),
      limit,
      search: searchTerm || null,
    },
    fetchPolicy: "network-only",
  });
  const projectAccessType: DomainProps =
    pagesByDomain?.pagesByDomain?.domain?.userAccesses;

  const [deletePage] = useDeletePage2Mutation({
    onCompleted: async () => {
      await refetchPagesByDomain();
      isLoadingOfDeletion(false);
    },
  });
  const [massDelete] = useMassDeleteFolderAndPageMutation({
    onCompleted: () => {
      setTimeout(async () => {
        await refetchPagesByDomain();
        isLoadingOfDeletion(false);
      }, 1000);
    },
  });
  const lng = useGetUrlLocale();

  useEffect(() => {
    document.title = "Project Page";
  }, []);
  const domainTitle = pagesByDomain?.pagesByDomain?.domain?.title;

  const handleSearch = () => {
    refetchPagesByDomain({
      search: searchTerm,
      page: currentPage,
      domainId: Number(projectId),
      limit,
    });
  };

  useDebounceEffect(handleSearch, 1000, [searchTerm]);

  const handleLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = Number(event.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
    refetchPagesByDomain({
      search: searchTerm,
      page: 1,
      domainId: Number(projectId),
      limit: newLimit,
    });
  };

  const handlePageChange = (pageNumber: number) => {
    setSelectedItems({
      pageIds: [],
      folderIds: [],
    });
    setCurrentPage(pageNumber);
  };

  const pagination = pagesByDomain?.pagesByDomain?.paginator;
  const pageCount = pagination?.totalPages || 0;
  const totalItems = pagination?.totalItemCount || 0;
  const firstItemIndex = pagination
    ? (currentPage - 1) * pagination.itemNumberPerPage + 1
    : 0;
  const lastItemIndex = pagination
    ? Math.min(currentPage * pagination.itemNumberPerPage, totalItems)
    : 0;

  const folders: FolderProps[] = useMemo(
    () =>
      pagesByDomain?.pagesByDomain?.folders.map((folder) => ({
        id: folder.id!,
        title: folder.title!,
        users: folder.userAccesses,
      })) || [],
    [pagesByDomain?.pagesByDomain?.folders]
  );

  const pages: PageProps[] = useMemo(
    () =>
      pagesByDomain?.pagesByDomain?.pagesWithoutFolder.map((page) => ({
        id: page.id!,
        title: page.title!,
        type: page.type,
        image: page.image || ImgPage,
        users: page.userAccesses,
        publishedAt: page.publishedAt,
        publicUrl: page.publicUrl,
      })) || [],
    [pagesByDomain?.pagesByDomain?.pagesWithoutFolder]
  );

  useEffect(() => {
    if (!pages.length && !folders.length && !loading) {
      setCurrentPage(1);
    }
  }, [pages, folders, loading]);

  const { data: User } = useUserQuery();
  const currentUser = User?.user?.email;
  const location = useLocation();

  const sharedValue = location.state?.shared;
  const accessType = pagesByDomain?.pagesByDomain?.domain?.userAccesses?.find(
    (user) => user?.user?.email === currentUser
  );
  const link = sharedValue || accessType ? `${lng}/shared` : `${lng}/projects`;

  const tryDeleteFiles = async () => {
    try {
      const { data } = await massDelete({ variables: selectedItems });

      if (data?.massDeleteFolderAndPage) {
        setShowDeleteModal(false);
        moveItem({ ...moveItemState, data: { folderIds: [], pageIds: [] } });
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="position-relative">
      <PageHeader
        accessType={accessType?.accessType}
        title={t("menu.myProjects")}
        paths={
          sharedValue || accessType
            ? [
                { label: t("menu.shared"), path: `${lng}/shared` },
                { label: domainTitle, path: `${lng}/projects/${projectId}` },
              ]
            : [
                { label: t("folder.projects"), path: `${lng}/projects` },
                { label: domainTitle, path: `${lng}/projects/${projectId}` },
              ]
        }
      />

      <SearchBar
        t={t}
        title={domainTitle}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        viewMode={viewMode}
        setViewMode={setViewMode}
        handleSearch={handleSearch}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        setShowDeleteModal={setShowDeleteModal}
        link={link}
        isDisabled={!(folders.length > 0 || pages.length > 0)}
      />
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status" />
        </div>
      ) : folders.length > 0 || pages.length > 0 ? (
        <div className={`${isLoading && "pe-none opacity-75"}`}>
          {viewMode === "grid" ? (
            <FolderPageListGrid
              folders={folders}
              pages={pages}
              folderImgSrc={ImgFolder}
              pageImgSrc={ImgPage}
              deletePage={deletePage}
              refetchPagesByDomain={refetchPagesByDomain}
              domainId={pagesByDomain?.pagesByDomain?.domain?.id}
              loading={loading}
              projectAccessType={projectAccessType}
              sharedValue={sharedValue}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
            />
          ) : (
            <FolderPageListTable
              folders={folders}
              pages={pages}
              folderImgSrc={ImgFolder}
              pageImgSrc={ImgPage}
              deletePage={deletePage}
              refetchPagesByDomain={refetchPagesByDomain}
              domainId={pagesByDomain?.pagesByDomain?.domain?.id}
              projectAccessType={projectAccessType}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
            />
          )}
          <Pagination
            t={t}
            pages={pageCount}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            firstItemIndex={firstItemIndex}
            lastItemIndex={lastItemIndex}
            totalItems={totalItems}
            limit={limit}
            onLimitChange={handleLimitChange}
          />
        </div>
      ) : searchTerm ? (
        <NotFoundPlug />
      ) : (
        <NoPages domainId={projectId} folderId={id} />
      )}
      <DeletePageFolderModal
        itemType={ITEMS.FILES}
        title={deleteModalTitle}
        description={deleteModalDescription}
        additionText={deleteModalAdditionText}
        showDeleteModal={showDeleteModal}
        handleCloseModal={() => setShowDeleteModal(false)}
        handleConfirmDelete={tryDeleteFiles}
      />
      <CustomToast
        isShow={haveBeenSaved}
        setShow={isHaveBeenSaved}
        message={`${t("share.beenSaved")}`}
        isSeenClose={false}
        background="bg-success-light"
        text="text-success fw-400 fs-6"
        check={true}
      />
    </div>
  );
};

export default ProjectPage;
